import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const GardenGroveVietnameseHomesPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Garden+Grove&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Garden Grove properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Garden Grove Vietnamese Homes | Real Estate Near Little Saigon | Truc Doan</title>
        <meta name="description" content="Find Garden Grove homes near Little Saigon. Family-friendly neighborhoods, Vietnamese community amenities, and great schools. Truc Doan, your local expert." />
        <meta name="keywords" content="Garden Grove homes for sale, Garden Grove Vietnamese community, Garden Grove real estate, homes near Little Saigon, Garden Grove CA houses, Vietnamese realtor Garden Grove, family homes Garden Grove, Truc Doan Garden Grove" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/garden-grove-vietnamese-homes`} />
        <meta property="og:title" content="Garden Grove Vietnamese Homes | Real Estate Near Little Saigon | Truc Doan" />
        <meta property="og:description" content="Discover Garden Grove homes near Little Saigon. Family-friendly neighborhoods with Vietnamese community amenities. Expert guidance from Truc Doan." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/garden-grove-vietnamese-homes`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Garden Grove, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/garden-grove-vietnamese-homes`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan helps Vietnamese families find homes in Garden Grove CA, near Little Saigon. Expert knowledge of family-friendly neighborhoods and community amenities.",
            "areaServed": {
              "@type": "City",
              "name": "Garden Grove, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Garden Grove Vietnamese Homes for Sale</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Discover family-friendly homes in Garden Grove — Orange County's gateway to Little Saigon and Vietnamese culture.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Buy a Home in Garden Grove?</h2>
          <p>Garden Grove is one of Orange County's most diverse and family-oriented cities, offering an exceptional quality of life at a relatively accessible price point. The southern portion of Garden Grove overlaps with the Little Saigon cultural district, giving residents immediate access to Vietnamese restaurants, markets, and community services while enjoying larger lot sizes and more spacious homes than neighboring Westminster.</p>
          <p>With a strong Vietnamese-American population, excellent public schools, and abundant parks, Garden Grove attracts families who want the best of both worlds — proximity to cultural amenities and the space to grow. The city's Strawberry Festival, community events, and well-maintained neighborhoods create a welcoming atmosphere for homeowners of all backgrounds.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Garden Grove Neighborhoods & Amenities</h2>
          <p>Garden Grove offers diverse neighborhoods ranging from historic single-family home communities to modern developments, all with easy access to Little Saigon.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Proximity to Little Saigon</h3>
              <p>Southern Garden Grove borders Westminster's Little Saigon district. Walk or drive minutes to authentic Vietnamese dining, shopping, and cultural events.</p>
            </div>
            <div className="amenity">
              <h3>Garden Grove Unified Schools</h3>
              <p>Highly rated schools including Bolsa Grande High School, Pacifica High School, and numerous elementary schools with strong academic programs and bilingual resources.</p>
            </div>
            <div className="amenity">
              <h3>Parks & Recreation</h3>
              <p>Garden Grove Park, Atlantis Play Center, and dozens of community parks offer sports fields, playgrounds, and family gathering spaces throughout the city.</p>
            </div>
            <div className="amenity">
              <h3>Strawberry Festival</h3>
              <p>The annual Garden Grove Strawberry Festival is a beloved community tradition celebrating the city's agricultural heritage with rides, food, and entertainment.</p>
            </div>
            <div className="amenity">
              <h3>Vietnamese Businesses & Dining</h3>
              <p>Brookhurst Street and surrounding areas feature Vietnamese restaurants, bakeries, and services. Enjoy phở, bún bò Huế, and chè within minutes of home.</p>
            </div>
            <div className="amenity">
              <h3>Freeway Access</h3>
              <p>Easy access to the 22, 5, and 405 freeways connects Garden Grove to jobs in Irvine, Anaheim, Long Beach, and Los Angeles.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Garden Grove Real Estate Market Snapshot</h2>
          <p>Garden Grove offers excellent value for families seeking larger homes near Little Saigon, with a strong market driven by community demand.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Median Home Price (Garden Grove)</td>
                <td>~$820,000</td>
              </tr>
              <tr>
                <td>Average Days on Market</td>
                <td>22–32 Days</td>
              </tr>
              <tr>
                <td>Most Popular Price Range</td>
                <td>$600K – $950K</td>
              </tr>
              <tr>
                <td>Property Types</td>
                <td>Single Family, Condos, Townhomes</td>
              </tr>
              <tr>
                <td>Top Draws</td>
                <td>Schools, Little Saigon Access, Space</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Current Homes for Sale in Garden Grove</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}, CA {property.postal_code}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>
              Browse all available Garden Grove listings by clicking "Search Listings" below.
            </p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Buy or Sell in Garden Grove?</h2>
          <p>Contact Truc Doan today for a free consultation. I specialize in helping Vietnamese families find their perfect home in Garden Grove and the Little Saigon area.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Garden Grove Real Estate Inquiry" className="cta-button primary">
              Email Truc Doan
            </a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">
              Call +1-818-818-0989
            </a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>
              Search Garden Grove Listings
            </button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default GardenGroveVietnameseHomesPage;
